import { action } from '@reatom/framework'

import { Array, AsyncStateN, Result } from '@blade/std-lib'
import { storeWalletRequestAction } from '@root/entities/wallet/atoms/wallet-requests'
import { refreshDeviceWalletsAction } from '@root/features/secure-storage/atoms'
import { WrongPasswordError } from '@root/features/secure-storage/secure-storage-error'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { Error, UnknownError } from '@root/shared'
import { reatomRequest } from '@root/shared/lib'
import { delugeTheme } from '@root/shared/ui-kit'

import type { Redacted } from '@blade/std-lib'
import type { Network } from '@root/entities/network'

const colors = [
  delugeTheme.palette.acid[9],
  delugeTheme.palette.cyan[9],
  delugeTheme.palette.amber[9],
  delugeTheme.palette.green[9],
  delugeTheme.palette.orange[9],
  delugeTheme.palette.pink[9],
  delugeTheme.palette.purple[9],
]

const generateDeviceWalletAction = action(
  async (
    ctx,
    {
      password,
      network,
      mnemonic,
    }: {
      password: Redacted.Redacted
      network: Network.Network
      mnemonic: string
    },
  ) => {
    const { secureStorageService } = ctx.get(dependenciesAtom)
    const walletResult = await ctx.schedule(() =>
      secureStorageService.createWallet(password, network, mnemonic),
    )

    return walletResult.pipe(
      Result.map((it) => ({
        wallet: it,
        mnemonic: mnemonic,
      })),
      Result.mapError((it) => {
        if (it instanceof WrongPasswordError) {
          return it
        }
        return UnknownError.fromUnknown(it)
      }),
    )
  },
  'generateDeviceWalletAction',
)

export const reatomCreateWallet = () => {
  const deviceWalletStateAtom = reatomRequest({
    execAction: generateDeviceWalletAction,
    // eslint-disable-next-line @reatom/unit-naming-rule
    name: 'generateDeviceWalletAction',
  })

  const {
    executeAction: confirmWalletCreationAction,
    requestStateAtom: confirmWalletCreationRequestState,
  } = reatomRequest({
    execAction: storeWalletRequestAction,
    name: 'storeWalletRequestAction',
    onResult: (ctx, result) => {
      const { secureStorageService } = ctx.get(dependenciesAtom)
      const state = ctx.get(deviceWalletStateAtom)
      if (!AsyncStateN.isSuccess(state)) {
        return
      }
      if (Result.isSuccess(result)) {
        refreshDeviceWalletsAction(ctx)
      } else {
        secureStorageService.removeWallet(state.value.wallet.walletId)
      }
    },
  })

  const finishCreationAction = action((ctx) => {
    const state = ctx.get(deviceWalletStateAtom)

    if (!AsyncStateN.isSuccess(state)) {
      return Result.fail(Error.UnknownError.fromUnknown('invalid state'))
    }

    const wallet = state.value.wallet
    return confirmWalletCreationAction(ctx, {
      walletId: wallet.walletId,
      walletAddress: wallet.address,
      name: wallet.name,
      network: wallet.network,
      color: Array.randomElement(colors)!,
    })
  }, 'finishCreationAction')

  return {
    generateWalletAction: deviceWalletStateAtom.execute,
    deviceWalletStateAtom,
    finishCreationAction,
    confirmWalletCreationRequestState,
  }
}

export type CreateWalletReatom = ReturnType<typeof reatomCreateWallet>
